/**
 * API utility for making authenticated requests to the backend.
 * Session JWT is sent via HttpOnly cookie (credentials: 'include').
 * Tokens are never read from or written to localStorage.
 */
import { devLog } from './devLog';
import { refreshSession } from './authSession';
import { notifySessionExpired, isAuthRedirectInProgress } from './authRedirect';
import {
  ensureCsrfToken,
  getCsrfHeaders,
  syncCsrfTokenFromCookie,
  rebootstrapCsrfToken,
  isCsrfValidationFailedResponse,
} from './csrf';
import { isRateLimitError, readRateLimitError } from './rateLimit';
import { persistRateLimitFromError } from './rateLimitStorage';
import { buildApiUrl } from './apiBaseUrl';

export { isRateLimitError };

/**
 * Prefer the first validation detail message when the API returns VALIDATION_ERROR.
 * @param {{ error?: string, code?: string, details?: Array<{ path?: string, message?: string }> }} data
 * @param {string} [fallback='Request failed']
 * @returns {string}
 */
export function getApiErrorMessage(data, fallback = 'Request failed') {
  if (!data) return fallback;
  if (data.code === 'VALIDATION_ERROR' && Array.isArray(data.details) && data.details.length > 0) {
    const first = data.details[0];
    const path = first.path && first.path !== '(root)' ? `${first.path}: ` : '';
    const detailMessage = first.message || fallback;
    return `${path}${detailMessage}`;
  }
  return data.error || fallback;
}

/**
 * Fetch init for authenticated requests via HttpOnly session cookie.
 * @param {RequestInit} [options]
 * @returns {RequestInit}
 */
export function getAuthenticatedFetchInit(options = {}) {
  const method = options.method || 'GET';
  const headers = {
    Accept: 'application/json',
    ...getCsrfHeaders(method),
    ...options.headers,
  };

  return {
    credentials: 'include',
    ...options,
    headers,
  };
}

function createClientRequestId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `web-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
}

/** Client-generated key for duplicate-submit protection on mutation endpoints. */
export function createIdempotencyKey() {
  return createClientRequestId();
}

function withIdempotencyHeader(options = {}, idempotencyKey) {
  if (!idempotencyKey) {
    return options;
  }
  return {
    ...options,
    headers: {
      ...options.headers,
      'Idempotency-Key': idempotencyKey,
    },
  };
}

function readResponseRequestId(response) {
  return (
    response?.headers?.get('X-Request-Id') ||
    response?.headers?.get('X-Correlation-Id') ||
    null
  );
}

/**
 * Delay helper for retry logic
 * @param {number} ms - Milliseconds to delay
 */
function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Check if an error should trigger a retry
 * @param {Error} error - The error to check
 * @param {Response|null} response - The response object (if available)
 * @returns {boolean} - Whether to retry
 */
function shouldRetry(error, response) {
  // Don't retry on authentication or rate-limit errors
  if (response && (response.status === 401 || response.status === 429)) {
    return false;
  }
  
  // Retry on network errors
  if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
    return true;
  }
  
  // Retry on empty response errors
  if (error.message && error.message.includes('empty response')) {
    return true;
  }
  
  // Retry on JSON parse errors (but not HTML errors)
  if (error.message && error.message.includes('Invalid response from server')) {
    return true;
  }
  
  // Retry on HTTP2 protocol errors (status 200 but network error)
  if (response && response.status === 200 && !error.message.includes('Server returned HTML')) {
    // Check if we got a 200 but couldn't parse - this is the intermittent issue
    if (error.message && (error.message.includes('parse') || error.message.includes('Invalid response'))) {
      return true;
    }
  }
  
  return false;
}

/**
 * Retry wrapper for API calls
 * @param {Function} apiCall - The API function to retry
 * @param {number} maxRetries - Maximum number of retries (default: 2)
 * @returns {Promise} - The result of the API call
 */
async function withRetry(apiCall, maxRetries = 2) {
  let lastError;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const result = await apiCall();
      return result;
    } catch (error) {
      lastError = error;
      
      // Extract response from error if available (for parseJsonResponse errors)
      const response = error.response || null;
      
      // Check if we should retry
      if (attempt < maxRetries && shouldRetry(error, response)) {
        const delayMs = (attempt + 1) * 500; // Exponential backoff: 500ms, 1000ms
        devLog.warn(
          `API call failed (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${delayMs}ms...`,
          error.message
        );
        await delay(delayMs);
        continue;
      }
      
      // Don't retry - throw the error
      throw error;
    }
  }
  
  // This should never be reached, but just in case
  throw lastError;
}

/**
 * Make authenticated API request using HttpOnly session cookie.
 * Handles 401 errors by redirecting to login.
 */
export async function apiRequest(endpoint, options = {}, _isRetry = false) {
  const method = options.method || 'GET';
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'X-Request-Id': createClientRequestId(),
    ...getCsrfHeaders(method),
    ...options.headers,
  };

  const fullUrl = buildApiUrl(endpoint);
  devLog.log(`API Request: ${method} ${endpoint}`);

  try {
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
      await ensureCsrfToken();
      Object.assign(headers, getCsrfHeaders(method));
    }

    const response = await fetch(fullUrl, getAuthenticatedFetchInit({
      ...options,
      headers,
    }));

    if (response.status === 429) {
      const rateLimitError = await readRateLimitError(response);
      persistRateLimitFromError(rateLimitError);
      throw rateLimitError;
    }

    if (response.status === 403 && !_isRetry) {
      let body = {};
      try {
        body = await response.clone().json();
      } catch {
        /* non-JSON 403 */
      }
      if (isCsrfValidationFailedResponse(response.status, body)) {
        const bootstrapped = await rebootstrapCsrfToken();
        if (bootstrapped) {
          syncCsrfTokenFromCookie();
          return apiRequest(endpoint, options, true);
        }
      }
    }

    if (response.status === 401) {
      if (isAuthRedirectInProgress()) {
        throw new Error('Session expired. Please log in again.');
      }
      if (!_isRetry) {
        const refreshed = await refreshSession();
        if (refreshed.ok) {
          syncCsrfTokenFromCookie();
          return apiRequest(endpoint, options, true);
        }
        if (refreshed.csrfRejected) {
          devLog.warn('api: refresh CSRF rejected; not redirecting to login');
          throw new Error('Session refresh temporarily unavailable. Please reload the page.');
        }
      }
      notifySessionExpired({ reason: 'api_401' });
      throw new Error('Session expired. Please log in again.');
    }

    return response;
  } catch (error) {
    // Handle network errors (connection refused, CORS, timeout, etc.)
    if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
      devLog.error('Network error:', error.message);
      
      // Provide more specific error message
      let errorMessage = 'Unable to connect to server. ';
      errorMessage += 'Please check if the backend server is running and try again.';
      
      throw new Error(errorMessage);
    }
    // Re-throw other errors
    throw error;
  }
}

/**
 * Parse JSON response with error handling
 * @param {Response} response - The fetch response object
 * @param {string} method - HTTP method (GET, POST, PUT, DELETE)
 */
async function parseJsonResponse(response, method = 'GET') {
  let jsonData;
  let text;
  try {
    // SAFE JSON PARSING: Read response.text() first instead of using response.json()
    text = await response.text();
    
    // Check if response body is empty
    if (!text || text.trim().length === 0) {
      // Empty response - only acceptable for DELETE operations
      if (method === 'DELETE') {
        return { success: response.ok, data: null, __httpStatus: response.status };
      }
      
      // For non-DELETE operations, empty response is an error
      const errorMessage = `Server returned empty response (Status: ${response.status}). Expected JSON data but received no content.`;
      devLog.error('Empty response error:', response.status, method);
      throw new Error(errorMessage);
    }
    
    // IMPROVED: Check if response is HTML (case-insensitive, handles various HTML formats)
    const textTrimmed = text.trim();
    const textLower = textTrimmed.toLowerCase();
    
    // Check for HTML responses (common when nginx serves index.html instead of proxying)
    if (textLower.startsWith('<!doctype') || 
        textLower.startsWith('<html') || 
        textLower.startsWith('<?xml') ||
        textTrimmed.startsWith('<!DOCTYPE') ||
        textTrimmed.startsWith('<html') ||
        (textTrimmed.includes('<html') && textTrimmed.includes('</html>'))) {
      
      devLog.error('Server returned HTML instead of JSON:', response.status);
      
      // Provide more helpful error message
      let errorMessage = `Server returned HTML instead of JSON (Status: ${response.status}). `;
      if (response.status === 404 || response.status === 200) {
        errorMessage += 'The API endpoint may not be configured correctly in nginx. Please check that /pm routes are proxied to the backend.';
      } else if (response.status === 500) {
        errorMessage += 'The server encountered an error. Please check backend logs.';
      } else {
        errorMessage += 'This may indicate a proxy/load balancer routing issue.';
      }
      
      throw new Error(errorMessage);
    }
    
    // SAFE JSON PARSING: Use JSON.parse() instead of response.json()
    jsonData = JSON.parse(text);
  } catch (error) {
    // JSON parsing failed - this is a real error
    devLog.error('Failed to parse JSON response:', error.message, response.status, method);
    
    // If error was already thrown by HTML check or empty response check, re-throw it
    if (error.message.includes('Server returned HTML') || error.message.includes('empty response')) {
      throw error;
    }
    
    throw new Error(`Invalid response from server: ${response.status} ${response.statusText}`);
  }
  
  // If HTTP status indicates error but JSON parsing succeeded, ensure success field reflects it
  if (!response.ok && jsonData.success !== false) {
    jsonData.success = false;
    if (!jsonData.error) {
      jsonData.error = `Server error: ${response.status} ${response.statusText}`;
    }
  }

  jsonData.__httpStatus = response.status;
  const requestId = readResponseRequestId(response) || jsonData.requestId;
  if (requestId) {
    jsonData.requestId = requestId;
  }
  if (!response.ok && requestId) {
    const base = jsonData.error || `Server error: ${response.status}`;
    if (!String(base).includes(requestId)) {
      jsonData.error = `${base} (requestId: ${requestId})`;
    }
  }
  return jsonData;
}

/**
 * Make GET request with authentication — single attempt (no retry).
 * Use for side-effecting or long-running GET endpoints (e.g. MS365 sync).
 */
export async function apiGetOnce(endpoint, options = {}) {
  const response = await apiRequest(endpoint, { method: 'GET', ...options });
  return parseJsonResponse(response, 'GET');
}

/**
 * Make GET request with authentication and retry logic
 */
export async function apiGet(endpoint) {
  return withRetry(async () => {
    const response = await apiRequest(endpoint, { method: 'GET' });
    try {
      return await parseJsonResponse(response, 'GET');
    } catch (error) {
      // Attach response to error for retry logic
      error.response = response;
      error.url = response.url;
      throw error;
    }
  });
}

/**
 * Make POST request with authentication and retry logic
 */
/**
 * @param {string} endpoint
 * @param {unknown} data
 * @param {{ idempotencyKey?: string } & RequestInit} [options]
 */
export async function apiPost(endpoint, data, options = {}) {
  const { idempotencyKey, ...requestOptions } = options;
  return withRetry(async () => {
    const response = await apiRequest(
      endpoint,
      withIdempotencyHeader(
        {
          method: 'POST',
          body: JSON.stringify(data),
          ...requestOptions,
        },
        idempotencyKey
      )
    );
    try {
      return await parseJsonResponse(response, 'POST');
    } catch (error) {
      // Attach response to error for retry logic
      error.response = response;
      error.url = response.url;
      throw error;
    }
  });
}

/**
 * Make PUT request with authentication and retry logic
 */
/**
 * @param {string} endpoint
 * @param {unknown} data
 * @param {{ idempotencyKey?: string } & RequestInit} [options]
 */
export async function apiPut(endpoint, data, options = {}) {
  const { idempotencyKey, ...requestOptions } = options;
  return withRetry(async () => {
    const response = await apiRequest(
      endpoint,
      withIdempotencyHeader(
        {
          method: 'PUT',
          body: JSON.stringify(data),
          ...requestOptions,
        },
        idempotencyKey
      )
    );
    try {
      return await parseJsonResponse(response, 'PUT');
    } catch (error) {
      // Attach response to error for retry logic
      error.response = response;
      error.url = response.url;
      throw error;
    }
  });
}

/**
 * Make PATCH request with authentication and retry logic
 */
export async function apiPatch(endpoint, data) {
  return withRetry(async () => {
    const response = await apiRequest(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    try {
      return await parseJsonResponse(response, 'PATCH');
    } catch (error) {
      error.response = response;
      error.url = response.url;
      throw error;
    }
  });
}

/**
 * Make DELETE request with authentication and retry logic
 */
export async function apiDelete(endpoint) {
  return withRetry(async () => {
    const response = await apiRequest(endpoint, { method: 'DELETE' });
    try {
      return await parseJsonResponse(response, 'DELETE');
    } catch (error) {
      // Attach response to error for retry logic
      error.response = response;
      error.url = response.url;
      throw error;
    }
  });
}















